import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Gift, Sparkles, PackageOpen, Truck, PenLine, ArrowRight, Mail, Loader2, Leaf } from "lucide-react";
import { api } from "@/lib/api";
import { useGet } from "@/lib/hooks";
import { Reveal } from "@/components/wp/Reveal";
import { SectionHeading } from "@/components/wp/SectionHeading";
import { ProtectedImage } from "@/components/wp/ProtectedImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const VALUES = [
  { icon: Gift, title: "Thoughtfully curated", copy: "Every parcel is hand-assembled from pieces we genuinely love — never filler." },
  { icon: PenLine, title: "Personal by design", copy: "A handwritten note and the option to make each gift deeply personal." },
  { icon: Leaf, title: "Made with care", copy: "Beautiful, considerate packaging that feels as good to open as to give." },
];

function NotifyForm({ initiative }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try { await api.post("/notify", { email: email.trim(), initiative: initiative || "our-story" }); toast.success("You're on the list — we'll be in touch 💝"); setEmail(""); }
    catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="mx-auto flex max-w-md flex-col gap-2 sm:flex-row">
      <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="your@email.com" required className="rounded-xl" data-testid="story-notify-email" />
      <Button type="submit" disabled={loading} className="rounded-xl bg-wp-rose-strong text-white hover:brightness-105" data-testid="story-notify-submit">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="mr-1.5 h-4 w-4" /> Keep me posted</>}
      </Button>
    </form>
  );
}

export default function OurStory() {
  const navigate = useNavigate();
  const { data: content } = useGet("/content");
  const { data: vision } = useGet("/vision");
  const steps = content?.packaging_steps || [];

  return (
    <div>
      {/* Hero */}
      <section className="wp-hero-gradient wp-noise relative overflow-hidden">
        <div className="wp-container relative py-16 text-center sm:py-24">
          <Reveal>
            <span className="wp-chip mx-auto"><Heart className="h-3.5 w-3.5 text-wp-rose-strong" /> Our Story</span>
            <h1 className="mx-auto mt-5 max-w-3xl font-display text-4xl leading-tight tracking-[-0.02em] sm:text-6xl">Gifting, made from the heart.</h1>
            <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-wp-ink-muted">
              Wonder Parcel began with a simple belief — that the most meaningful gifts aren't the most expensive, but the most thoughtful. We set out to make it effortless to send something truly beautiful, and truly personal.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Mission */}
      <section className="wp-section">
        <div className="wp-container grid items-center gap-10 lg:grid-cols-2">
          <Reveal>
            <span className="wp-label text-xs text-wp-rose-strong">Our mission</span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl">To turn everyday moments into memories worth keeping.</h2>
            <p className="mt-4 leading-relaxed text-wp-ink-muted">
              Behind every parcel is a small team obsessed with the little details — the ribbon that's tied just right, the note written by hand, the keepsake chosen because it means something. We pair beautiful pieces into ready-to-give gifts, then make it easy to add your own personal touch, so what arrives feels unmistakably from you.
            </p>
            <Button onClick={() => navigate("/shop")} className="mt-6 rounded-xl bg-wp-rose-strong text-white hover:brightness-105" data-testid="story-explore-button">Explore our gifts <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
          </Reveal>
          <div className="grid grid-cols-3 gap-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="flex h-full flex-col items-center gap-2 rounded-wp-lg border border-border bg-card p-5 text-center shadow-wp-soft">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-wp-blush-100 text-wp-rose-strong"><v.icon className="h-5 w-5" /></span>
                  <p className="font-display text-sm leading-tight">{v.title}</p>
                  <p className="text-xs leading-relaxed text-wp-ink-muted">{v.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Packaging experience */}
      {steps.length > 0 && (
        <section className="wp-section bg-wp-surface">
          <div className="wp-container">
            <SectionHeading eyebrow="The unboxing" title="Every parcel, a little ceremony" subtitle="From our hands to theirs, here's the care that goes into every gift." align="center" />
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {steps.map((st, i) => (
                <Reveal key={i} delay={(i % 4) * 0.06}>
                  <div className="h-full rounded-wp-lg border border-border bg-card p-6 shadow-wp-soft">
                    <span className="grid h-10 w-10 place-items-center rounded-full bg-wp-rose-strong/10 font-display text-wp-rose-strong">{i + 1}</span>
                    <h3 className="mt-3 font-display text-lg">{st.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-wp-ink-muted">{st.desc || st.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Vision */}
      {(vision || []).length > 0 && (
        <section className="wp-section">
          <div className="wp-container">
            <SectionHeading eyebrow="Where we're headed" title="Our vision" subtitle="We're only getting started. Here's what we're dreaming up next." align="center" />
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {(vision || []).map((v, i) => (
                <Reveal key={v.slug} delay={(i % 3) * 0.06}>
                  <div className="h-full overflow-hidden rounded-wp-lg border border-border bg-card shadow-wp-soft">
                    {v.image && <div className="aspect-[16/10] overflow-hidden bg-wp-cream-100"><ProtectedImage src={v.image} alt={v.title} className="h-full w-full object-cover" /></div>}
                    <div className="p-5">
                      <span className="wp-chip"><Sparkles className="h-3 w-3 text-wp-ocean" /> Coming soon</span>
                      <h3 className="mt-2 font-display text-xl">{v.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-wp-ink-muted">{v.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="mt-10 rounded-wp-xl border border-border bg-wp-surface p-8 text-center">
              <h3 className="font-display text-2xl">Be the first to know</h3>
              <p className="mx-auto mt-1.5 mb-5 max-w-md text-sm text-wp-ink-muted">Leave your email and we'll gently let you know when something new arrives.</p>
              <NotifyForm initiative="our-story" />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
